import './styles/graphic.scss';
import json from './data/data.json';


const params = new URLSearchParams(location.search);
const area = params.get('area');

const data = json;

const groupTitles = {
  idf: 'Killed by Israeli strike or ground operation',
  wounded: 'Died from wounds or lack of care',
  captors: 'Killed by captors',
  unknown: 'Cause of death unconfirmed',
};

const groupOrder = ['idf', 'captors', 'wounded', 'unknown'];

function groupKey(person) {
  return String(person.grouping || '').trim().toLowerCase();
}

function sheetOrder(person) {
  const value = Number(person.order);
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

function bySheetOrder(a, b) {
  return sheetOrder(a) - sheetOrder(b);
}

function isIdfYes(person) {
  return String(person.idf || '').trim().toLowerCase() === 'yes';
}

// Sheet text may include [label](https://example.com). Everything else stays plain text.
function appendBioLine(paragraph, line) {
  const pattern = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;
  let last = 0;
  let match = pattern.exec(line);

  while (match) {
    if (match.index > last) {
      paragraph.node().appendChild(document.createTextNode(line.slice(last, match.index)));
    }
    paragraph.append('a')
      .attr('href', match[2])
      .attr('target', '_blank')
      .attr('rel', 'noopener noreferrer')
      .text(match[1]);
    last = match.index + match[0].length;
    match = pattern.exec(line);
  }

  if (last < line.length) {
    paragraph.node().appendChild(document.createTextNode(line.slice(last)));
  }
}

// Height, left, and top as percentages of the circle. Tuned so the face fills the frame.
const photoCrops = {
  'Carmel Gat': ['270%', '-155%', '-25%'],
  'Elad Katzir': ['100%', '-4%', '0%'],
  'Hersh Goldberg-Polin': ['125%', '-55%', '-10%'],
  'Alexander Lobanov': ['250%', '-65%', '-45%'],
  'Cpl. Noa Marciano': ['230%', '-120%', '0%'],
  'Kfir Bibas': ['150%', '-40%', '0%'],
  'Ariel Bibas': ['165%', '-30%', '-10%'],
  'Shiri Bibas': ['175%', '-65%', '0%'],
  'Samer Talalka': ['120%', '-52%', '0%'],
  'Eden Zecharya': ['140%', '-115%', '0%'],
  'Elia Toledano': ['100%', '-6%', '0%'],
  'Ofir Tzarfati': ['155%', '-80%', '-25%'],
  'Almog Sarusi': ['200%', '-128%', '-100%'],
  'Yoram Metzger': ['140%', '0%', '-18%'],
  'Cpl. Nick Beiser': ['230%', '-50%', '0%'],
  'Yossi Sharabi': ['120%', '-20%', '0%'],
  'Youssef Ziyadne': ['160%', '-28%', '-8%'],
  'Hamza Ziyadne': ['190%', '-30%', '0%'],
  'Yotam Haim': ['120%', '-60%', '-10%'],
  'Sgt. Ron Sherman': ['170%', '-35%', '-10%'],
  'Eden Yerushalmi': ['140%', '-15%', '0%'],
  'Oded Lifshitz': ['140%', '-20%', '0%'],
  'Amiram Cooper': ['140%', '0%', '-10%'],
  'Itay Svirsky': ['110%', '2%', '-5%'],
};

// Face brightness in the visible circle, pulled toward the middle of the group.
// 1 is unchanged. Capped so a very dark photo is lifted without washing out.
const photoBright = {
  'Abraham Munder': 0.90,
  'Alexander Lobanov': 0.82,
  'Almog Sarusi': 1.04,
  'Alon Shimriz': 0.98,
  'Amiram Cooper': 0.87,
  'Ariel Bibas': 0.87,
  'Arye Zalmanovich': 1.18,
  'Bipin Joshi': 1.11,
  'Carmel Gat': 0.91,
  'Cpl. Nick Beiser': 1.49,
  'Eden Yerushalmi': 1.09,
  'Elad Katzir': 0.85,
  'Eden Zecharya': 0.87,
  'Elia Toledano': 1.09,
  'Guy Illouz': 1.07,
  'Hamza Ziyadne': 0.82,
  'Hersh Goldberg-Polin': 1.00,
  'Itay Svirsky': 0.82,
  'Itzhak Elgarat': 0.82,
  'Kfir Bibas': 0.82,
  'Oded Lifshitz': 1.18,
  'Ori Danino': 0.85,
  'Ofir Tzarfati': 0.91,
  'Sahar Baruch': 1.11,
  'Shiri Bibas': 0.82,
  'Tamir Nimrodi': 1.18,
  'Tsachi Idan': 1.10,
  'Yagev Buchstab': 0.85,
  'Yossi Sharabi': 0.82,
  'Yotam Haim': 1.32,
  'Youssef Ziyadne': 0.85,
};

// Pulls white backgrounds down toward the rest of the set. 1 is unchanged.
const photoContrast = {
  'Almog Sarusi': 0.72,
  'Alon Shimriz': 0.72,
  'Carmel Gat': 0.72,
  'Eden Yerushalmi': 0.72,
  'Elad Katzir': 0.72,
  'Guy Illouz': 0.72,
  'Hersh Goldberg-Polin': 0.72,
  'Oded Lifshitz': 0.72,
  'Ori Danino': 0.72,
  'Sahar Baruch': 0.72,
  'Yotam Haim': 0.72,
};

function cropStyle(person) {
  const crop = photoCrops[person.name];
  const bright = photoBright[person.name];
  const contrast = photoContrast[person.name];
  const parts = [];
  if (crop) parts.push(`--ch:${crop[0]}`, `--cx:${crop[1]}`, `--cy:${crop[2]}`);
  if (bright) parts.push(`--bright:${bright}`);
  if (contrast) parts.push(`--contrast:${contrast}`);
  return parts.length ? parts.join(';') : null;
}

export default function (props, container) {
  // Update the hostage count in the title
  const hostageCountElement = document.querySelector('#hostage-count');
  if (hostageCountElement) {
    hostageCountElement.textContent = data.length;
  }

  const idfCountElement = document.querySelector('#idf-count');
  if (idfCountElement) {
    idfCountElement.textContent = data.filter(isIdfYes).length;
  }

  // Create one modal at container level to overlay entire graphic
  const modal = container.append('div')
    .attr('class', 'modal')
    .style('display', 'none');

  const modalContent = modal.append('div')
    .attr('class', 'modal-content');

  const closeBtn = modalContent.append('span')
    .attr('class', 'modal-close')
    .html('&times;')
    .on('click', () => {
      modal.style('display', 'none');
    });

  // Create header container for image and basic info
  const modalHeader = modalContent.append('div')
    .attr('class', 'modal-header');

  const modalFrame = modalHeader.append('div')
    .attr('class', 'modal-image-frame');

  const modalImage = modalFrame.append('img');

  const modalInfo = modalHeader.append('div')
    .attr('class', 'modal-info');

  const modalName = modalInfo.append('h3')
    .attr('class', 'modal-name');

  const modalAge = modalInfo.append('p')
    .attr('class', 'modal-age');

  const modalStatus = modalInfo.append('p')
    .attr('class', 'modal-status');

  const modalIdfNote = modalInfo.append('p')
    .attr('class', 'modal-idf-note');

  // Bio container goes outside the header
  const modalNote = modalContent.append('div')
    .attr('class', 'modal-bio-container');

  // Add navigation controls
  const modalNav = modalContent.append('div')
    .attr('class', 'modal-nav');

  const prevBtn = modalNav.append('button')
    .attr('class', 'modal-nav-btn modal-nav-btn-prev')
    .html('<svg class="arrow-left" width="20" height="18" aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" fill="currentColor" viewBox="0 0 20 18"><path d="M19.7967 9.06344L11.9428 16.9388C11.6717 17.2105 11.2324 17.2105 10.961 16.9388C10.69 16.667 10.69 16.2263 10.961 15.9545L17.5835 9.25701H0.68964C0.161428 9.25701 0 8.91844 0 8.60202C0 8.24595 0.283927 7.88559 0.68964 7.88559H17.5863L10.961 1.18812C10.69 0.916335 10.69 0.475623 10.961 0.203838C11.2324 -0.0679461 11.6717 -0.0679461 11.9428 0.203838L19.7967 8.07916C20.0678 8.35095 20.0678 8.79166 19.7967 9.06344Z"></path></svg>');

  const navCounter = modalNav.append('span')
    .attr('class', 'modal-nav-counter')
    .style('color', '#666')
    .style('font-size', '14px');

  const nextBtn = modalNav.append('button')
    .attr('class', 'modal-nav-btn modal-nav-btn-next')
    .html('<svg class="arrow-right" width="20" height="18" aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" fill="currentColor" viewBox="0 0 20 18"><path d="M19.7967 9.06344L11.9428 16.9388C11.6717 17.2105 11.2324 17.2105 10.961 16.9388C10.69 16.667 10.69 16.2263 10.961 15.9545L17.5835 9.25701H0.68964C0.161428 9.25701 0 8.91844 0 8.60202C0 8.24595 0.283927 7.88559 0.68964 7.88559H17.5863L10.961 1.18812C10.69 0.916335 10.69 0.475623 10.961 0.203838C11.2324 -0.0679461 11.6717 -0.0679461 11.9428 0.203838L19.7967 8.07916C20.0678 8.35095 20.0678 8.79166 19.7967 9.06344Z"></path></svg>');

  // Track current person and all people
  let currentPersonIndex = 0;
  let allPeople = [];

  // Function to update modal content
  function updateModalContent(person, index) {
    currentPersonIndex = index;

    const key = groupKey(person);

    modalFrame.attr('class', `modal-image-frame${isIdfYes(person) ? ' idf-yes' : ''}`);
    modalImage
      .attr('src', person.image)
      .attr('alt', person.name)
      .attr('class', photoCrops[person.name] ? 'crop' : null)
      .attr('style', cropStyle(person));
    modalName.text(person.name).attr('class', `modal-name ${key}`);

    modalAge.html(typeof (person.age) === 'number' ? `Age ${person.age}` : person.age);
    modalStatus.text(groupTitles[key] || person.grouping || '');

    const showIdfNote = isIdfYes(person) && key !== 'idf';
    modalIdfNote
      .style('display', showIdfNote ? null : 'none')
      .html('');
    if (showIdfNote) {
      modalIdfNote.text('Death linked to Israeli military actions, including strikes and operations that led to captors killing their hostages.');
    }

    // Clear previous bio content and split by line breaks
    modalNote.html('');
    const bioLines = person.bio ? person.bio.split('\n').filter(line => line.trim()) : [];
    bioLines.forEach((line) => {
      const paragraph = modalNote.append('p')
        .attr('class', line === 'Biography not available' ? 'modal-bio note' : 'modal-bio');
      appendBioLine(paragraph, line);
    });

    // Update counter
    navCounter.text(`${index + 1} of ${allPeople.length}`);

    // Update button states
    prevBtn.attr('disabled', index === 0 ? true : null);
    nextBtn.attr('disabled', index === allPeople.length - 1 ? true : null);
  }

  // Use native event listeners for proper event.stopPropagation support
  prevBtn.node().addEventListener('click', (e) => {
    e.stopPropagation();
    if (currentPersonIndex > 0) {
      updateModalContent(allPeople[currentPersonIndex - 1], currentPersonIndex - 1);
    }
  });

  nextBtn.node().addEventListener('click', (e) => {
    e.stopPropagation();
    if (currentPersonIndex < allPeople.length - 1) {
      updateModalContent(allPeople[currentPersonIndex + 1], currentPersonIndex + 1);
    }
  });

  // Prevent modal content from closing when clicked
  modalContent.node().addEventListener('click', (e) => {
    e.stopPropagation();
  });

  // Close modal when clicking on backdrop
  modal.node().addEventListener('click', () => {
    modal.style('display', 'none');
  });

  const groupedData = data.reduce((acc, person) => {
    const key = groupKey(person);
    if (!acc[key]) acc[key] = [];
    acc[key].push(person);
    return acc;
  }, {});

  Object.values(groupedData).forEach(people => people.sort(bySheetOrder));

  const extraGroups = Object.keys(groupedData).filter(key => !groupOrder.includes(key));
  const sections = groupOrder.concat(extraGroups);

  allPeople = sections.reduce((people, key) => people.concat(groupedData[key] || []), []);

  sections.forEach((key) => {
    const people = groupedData[key];
    if (!people) return;

    const groupSection = container.append('div')
      .attr('class', `group-section ${key}`);

    const groupTitleElement = groupSection.append('h2')
      .attr('class', 'group-title');

    groupTitleElement.append('span')
      .text(groupTitles[key] || key);

    groupTitleElement.append('span')
      .attr('class', 'group-count')
      .text(` ${people.length}`);

    const profilesContainer = groupSection.append('div')
      .attr('class', 'profiles-container');

    people.forEach((person) => {
      const prof = profilesContainer.append('div')
        .attr('class', `profile ${key}`)
        .style('cursor', 'pointer')
        .on('click', () => {
          const personIndex = allPeople.findIndex(p => p === person);

          updateModalContent(person, personIndex);

          const groupRect = groupSection.node().getBoundingClientRect();
          const modalContentNode = modalContent.node();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

          modalContentNode.style.position = 'absolute';
          modalContentNode.style.top = `${groupRect.top + scrollTop + groupRect.height / 2}px`;
          modalContentNode.style.left = `${groupRect.left + groupRect.width / 2}px`;
          modalContentNode.style.transform = 'translate(-50%, -50%)';

          modal.style('display', 'block');
        });

      const image = prof.append('div')
        .attr('class', `image-container${isIdfYes(person) ? ' idf-yes' : ''}`)
        .attr('id', person.name.replace(/\s+/g, '-').toLowerCase());

      image.append('img')
        .attr('src', `${person.image}`)
        .attr('alt', person.name)
        .attr('class', photoCrops[person.name] ? 'crop' : null)
        .attr('style', cropStyle(person));

      const labels = prof.append('div')
        .attr('class', 'titles');

      labels.append('span')
        .attr('class', 'name')
        .text(`${person.name}`);

      labels.append('span')
        .attr('class', 'age')
        .html(person.age);
    });
  });
}
